import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MaintenanceService } from '../core/maintenance.service';

/**
 * HTTP statuses that mean "the API cannot be reached".
 *
 * 503 is the explicit maintenance answer. Status 0 is included because the
 * ALB in front of the API serves its 503 without Access-Control-Allow-Origin
 * headers (verified on acceptance, 2026-09-14): the browser blocks the
 * cross-origin response and HttpClient reports status 0 instead of 503.
 * Status 0 also covers offline / DNS failures / ad-blockers, which is why the
 * page copy says "trouble reaching our servers" rather than promising a
 * maintenance window. In all of those cases the widget is unusable anyway.
 */
export const MAINTENANCE_HTTP_STATUSES: ReadonlyArray<number> = [503, 0];

/**
 * Registered last (innermost) so it sees the raw backend error before the
 * other interceptors. Raises the global maintenance flag and rethrows, so
 * every effect still receives its regular failure action.
 *
 * Every HttpClient call in this app targets the Beachy API (Stripe and Google
 * Maps do not go through HttpClient), so there is no need to filter by host.
 */
@Injectable()
export class MaintenanceInterceptor implements HttpInterceptor {
  private maintenanceService = inject(MaintenanceService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && MAINTENANCE_HTTP_STATUSES.includes(error.status)) {
          this.maintenanceService.enterMaintenance();
        }

        return throwError(() => error);
      }),
    );
  }
}
